import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { get } from '../lib/api';
import { useAuth } from '../lib/auth';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PermissionsResponse {
  roles: string[];
  permissions: string[];
}

interface PermissionsContextValue {
  /** True if permissions have not yet loaded from the server. Default to deny. */
  loading: boolean;
  /** Role IDs assigned to the current user (e.g. 'role_admin', 'role_super_admin') */
  roles: string[];
  /** True if the current user has the given permission string */
  can: (permission: string) => boolean;
  /** True if the current user has ANY of the given permissions */
  canAny: (permissions: string[]) => boolean;
  /** True if the current user has ALL of the given permissions */
  canAll: (permissions: string[]) => boolean;
  /** Re-fetch permissions from the server (call after role changes) */
  refresh: () => Promise<void>;
}

// ── Context ───────────────────────────────────────────────────────────────────

const PermissionsContext = createContext<PermissionsContextValue | null>(null);

// ── Provider ──────────────────────────────────────────────────────────────────

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [permSet, setPermSet] = useState<Set<string>>(new Set());
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user?.userId) {
      // Not logged in — deny everything and mark as resolved (not loading)
      setPermSet(new Set());
      setRoles([]);
      setLoading(false);
      return;
    }
    try {
      const res = await get<PermissionsResponse>(`/api/users/${user.userId}/permissions`);
      setPermSet(new Set(res.permissions ?? []));
      setRoles(res.roles ?? []);
    } catch {
      // On error, keep existing permissions rather than locking the user out.
      // The deny-by-default policy during initial load (loading=true) is
      // sufficient — once loaded we don't regress to deny on transient errors.
    } finally {
      setLoading(false);
    }
  }, [user?.userId]);

  // Re-fetch whenever the logged-in user changes
  useEffect(() => {
    setLoading(true);
    refresh();
  }, [refresh]);

  // ── Permission checkers — deny during load (secure default) ─────────────────
  const can = useCallback(
    (permission: string) => !loading && permSet.has(permission),
    [loading, permSet],
  );

  const canAny = useCallback(
    (permissions: string[]) => !loading && permissions.some((p) => permSet.has(p)),
    [loading, permSet],
  );

  const canAll = useCallback(
    (permissions: string[]) => !loading && permissions.every((p) => permSet.has(p)),
    [loading, permSet],
  );

  return (
    <PermissionsContext.Provider value={{ loading, roles, can, canAny, canAll, refresh }}>
      {children}
    </PermissionsContext.Provider>
  );
}

// ── Internal accessor (used by usePermissions hook and PermissionGate) ────────

export function usePermissionsContext(): PermissionsContextValue {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    throw new Error('usePermissionsContext must be used within a PermissionsProvider');
  }
  return ctx;
}
